import { ApiClient, ApiStage, assertEx, delay } from '@xylabs/sdk-js'
import axios, { AxiosError } from 'axios'

import { CoinSponsorStatus, XyoWorldGeotokenSettingsJson } from '../models'
import { Quadkey } from '../Quadkey'

export class XyoWorldGeotokenSettingsClient extends ApiClient {
  public static geotokenSettingsWrappers = new Map<string, XyoWorldGeotokenSettingsClient>()

  private static apiUrls: { [key: string]: string } = {
    dev: 'https://dev-api.world.xyo.network/geotoken/',
    localhost: 'http://localhost:3030/dev/geotoken/',
    prod: 'https://api.world.xyo.network/geotoken/',
  }

  public data?: XyoWorldGeotokenSettingsJson

  public quadkey: Quadkey

  private loadingMeta = false

  private constructor(quadkey: Quadkey, token?: string | null, stage?: ApiStage, data?: XyoWorldGeotokenSettingsJson) {
    super(token, stage)
    this.quadkey = quadkey
    this.data = data
  }

  public static get(quadkey: Quadkey, token?: string | null, stage?: ApiStage, data?: XyoWorldGeotokenSettingsJson) {
    const result =
      XyoWorldGeotokenSettingsClient.geotokenSettingsWrappers.get(quadkey.toBase4HashLabel()) ??
      new XyoWorldGeotokenSettingsClient(quadkey, token, stage, data)
    XyoWorldGeotokenSettingsClient.geotokenSettingsWrappers.set(quadkey.toBase4HashLabel(), result)
    result.token = token
    return result
  }

  public static async getAll(authToken?: string | null, stage?: ApiStage): Promise<XyoWorldGeotokenSettingsClient[]> {
    const endPoint = `${XyoWorldGeotokenSettingsClient.apiUrls[stage ?? ApiStage.Prod]}`
    const response = await axios.get<XyoWorldGeotokenSettingsJson[]>(endPoint, {
      headers: authToken ? { Authorization: authToken } : {},
    })
    return response.data.map((data: XyoWorldGeotokenSettingsJson) => {
      const quadkey = Quadkey.fromBase4String(data.quadKey)
      return this.get(assertEx(quadkey), authToken, stage ?? ApiStage.Prod, data)
    })
  }

  public endPoint() {
    return `${XyoWorldGeotokenSettingsClient.apiUrls[this.stage]}${this.quadkey.toBase4HashLabel()}`
  }

  public getCoinSponsorStatus() {
    if (this.data?._coinSponsorStatus === CoinSponsorStatus.Approved || this.data?._coinSponsorStatus === CoinSponsorStatus.Disapproved) {
      return this.data?._coinSponsorStatus
    }
    if (this.data?.description && this.data?.image && this.data?.link && this.data?.sponsor && this.data?.title) {
      return CoinSponsorStatus.Submitted
    }
    return CoinSponsorStatus.Incomplete
  }

  public async load(clear = false) {
    if (clear) {
      this.data = undefined
    }
    if (this.data) {
      return this.data
    }
    if (!this.loadingMeta) {
      this.loadingMeta = true
      try {
        const response = await axios.get<XyoWorldGeotokenSettingsJson>(this.endPoint(), {
          headers: this.token ? { Authorization: this.token } : {},
        })
        this.data = response.data
      } catch (ex) {
        const error = ex as AxiosError
        if (error.response?.status === 404) {
          this.data = {}
        } else {
          console.error(JSON.stringify(error.response))
        }
      } finally {
        this.loadingMeta = false
      }
    } else {
      while (this.loadingMeta) {
        await delay(100)
      }
    }
    return this.data
  }

  public async save() {
    const postData: XyoWorldGeotokenSettingsJson = {
      ...this.data,
    }

    // convert the files to base64 for transport
    if (postData.files) {
      const base64Files: string[] = []
      for (const file of postData.files) {
        base64Files.push(file.toString('base64'))
      }
      postData.files = base64Files
    }

    console.log(`settings.save: ${this.token}`)

    await axios.post<XyoWorldGeotokenSettingsJson>(this.endPoint(), postData, {
      headers: this.token ? { Authorization: this.token } : {},
    })
    return this.data
  }

  public async setCoinSponsorStatus(coinSponsorStatus: CoinSponsorStatus) {
    const postData: XyoWorldGeotokenSettingsJson = {
      _coinSponsorStatus: coinSponsorStatus,
    }

    await axios.post<XyoWorldGeotokenSettingsJson>(`${this.endPoint()}/coinsponsorstatus`, postData, {
      headers: this.token ? { Authorization: this.token } : {},
    })
    return this.data
  }
}
